import { App } from "antd";
import { useCallback } from "react";
import { Point, Route } from "tm-shared/types";

import { ROUTES } from "@/constants";
import { useAppSelector } from "@/hooks";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { Meta } from "@/shared/seo";

import { MultiSelectorHeader } from "../../components/MultiSelectorHeader";
import { useScheduleSelection } from "../../hooks";
import { RouteEditorCapabilities } from "../../RouteEditor.policy";
import {
    RouteEditorModal,
    RouteEditorModalState,
} from "../../RouteEditor.types";

export type RouteEditorEditLoaderAndMetaBlockProps = {
    initialLoading: boolean;
    isDeleteBlocked: boolean;
    capabilities: RouteEditorCapabilities;
    selection: ReturnType<typeof useScheduleSelection>;
    points: Record<string, Point>;
    currentRoute: Route;
    onSetModalState: (state: RouteEditorModalState) => void;
};

export const RouteEditorEditLoaderAndMetaBlock = ({
    initialLoading,
    isDeleteBlocked,
    capabilities,
    selection,
    points,
    currentRoute,
    onSetModalState,
}: RouteEditorEditLoaderAndMetaBlockProps) => {
    const { notification } = App.useApp();

    const currentScheduleList = useAppSelector(
        (s) => s.manager.currentScheduleList,
    );

    const canDelete =
        !isDeleteBlocked || !capabilities.isDeleteRestrictedByPlan;

    const handleOpenDeleteModal = useCallback(() => {
        if (!canDelete) {
            notification.warning({
                title: "Видалення заблоковане",
                description: "План на територію вже затверджено",
                duration: 4,
            });
            return;
        }

        const selectedSchedules = selection.selectedSchedules;
        const hasDebt = selectedSchedules.some(
            ({ pointId }) => points[pointId]?.hasDebt,
        );

        if (hasDebt) {
            onSetModalState({
                type: RouteEditorModal.WarningDebt,
                payload: { isMultiSelect: selectedSchedules.length > 1 },
            });
        } else {
            onSetModalState({
                type: RouteEditorModal.DeleteSchedule,
                payload: {
                    pointIds: selectedSchedules.map(({ pointId }) => pointId),
                    currentRouteId: currentRoute?.uuid,
                    isEmptyAfterDelete:
                        currentScheduleList.length === selectedSchedules.length,
                    createMode: false,
                },
            });
        }
    }, [
        canDelete,
        currentRoute?.uuid,
        currentScheduleList.length,
        points,
        onSetModalState,
        selection.selectedSchedules,
        notification,
    ]);

    const handleOpenMoveModal = useCallback(() => {
        onSetModalState({
            type: RouteEditorModal.MoveSchedule,
            payload: {
                pointIds: selection.selectedSchedules.map(
                    ({ pointId }) => pointId,
                ),
                currentRouteId: currentRoute.uuid,
            },
        });
    }, [currentRoute.uuid, onSetModalState, selection.selectedSchedules]);

    return (
        <>
            {initialLoading && <Loading />}
            <Meta title="Редагувати маршрут" />
            {selection.multiSelectMode ? (
                <MultiSelectorHeader
                    selectedCount={selection.selectedSchedules.length}
                    allSelected={
                        selection.sortedAndFilteredSchedules.length ===
                        selection.selectedSchedules.length
                    }
                    onSelectAllItems={selection.handleSelectAll}
                    onClearSelection={selection.handleClearSelection}
                    onExitSelectionMode={selection.exitMultiSelectMode}
                    onDeleteSelected={handleOpenDeleteModal}
                    onMoveSelected={
                        capabilities.canMoveSchedules
                            ? handleOpenMoveModal
                            : undefined
                    }
                />
            ) : (
                <Header
                    title="Редагувати маршрут"
                    path={
                        capabilities.canEditAgent
                            ? ROUTES.RouteManagement
                            : undefined
                    }
                />
            )}
        </>
    );
};
